import type { ReactNode } from 'react';
import type { Lead } from '../../../types';

interface HotLeadsProps {
  leads: Lead[];
  loading?: boolean;
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600 bg-green-100';
  if (score >= 60) return 'text-yellow-600 bg-yellow-100';
  if (score >= 40) return 'text-orange-600 bg-orange-100';
  return 'text-red-600 bg-red-100';
}

function getStatusBadge(status: string): { color: string; label: string } {
  switch (status) {
    case 'new':
      return { color: 'bg-blue-100 text-blue-700', label: 'New' };
    case 'contacted':
      return { color: 'bg-yellow-100 text-yellow-700', label: 'Contacted' };
    case 'qualified':
      return { color: 'bg-green-100 text-green-700', label: 'Qualified' };
    default:
      return { color: 'bg-gray-100 text-gray-700', label: status };
  }
}

export function HotLeads({ leads, loading }: HotLeadsProps): ReactNode {
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Hot Leads</h3>
        <a href="/leads" className="text-sm text-ds-primary hover:text-ds-primary/80">
          View all →
        </a>
      </div>

      {loading ? (
        <div className="py-8 text-center text-gray-500">
          Loading leads...
        </div>
      ) : leads.length === 0 ? (
        <div className="py-8 text-center text-gray-500">
          <p>No hot leads found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {leads.map((lead) => {
            const statusBadge = getStatusBadge(lead.status);
            return (
              <a
                key={lead.id}
                href={`/leads/${lead.id}`}
                className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${getScoreColor(lead.score)}`}
                  >
                    {lead.score}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">
                      Lead #{lead.id.slice(-6)}
                    </p>
                    <p className="text-sm text-gray-500 capitalize">
                      Source: {lead.source}
                    </p>
                  </div>
                </div>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${statusBadge.color}`}
                >
                  {statusBadge.label}
                </span>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
